import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import {getLocaleText} from '../../util/translation';
import {handleTokenResult} from './square';
import {type TokenResult, type CashAppPay, type TokenizationEvent, PaymentMethodAlreadyDestroyedError, PaymentMethodUnsupportedError, PaymentMethodNotAttachedError, type Payments} from '@square/web-payments-sdk-types';
import cashappImageURL from '../../../img/marks/cashapp-green.svg';
import cashappImageLogoOnlyURL from '../../../img/marks/cashapp-green-logo-only.svg';
import {GatewayEligibility, type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature} from '../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../@type/features';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {store} from '../../store';
import {render} from '../../reducers/rootReducer';
import {type PaymentRequest} from '@square/web-payments-sdk-types';
import {debounce} from '../../util/debounce';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';

function initSquareCashAppMethod(): SquareGateway {
	let cashapp: CashAppPay | null = null;
	let buttonInstanceWaitingInterval: NodeJS.Timer | undefined;

	// Resolve/reject callbacks for cashapp token promise. Null represents payment not in the tokenization state
	let tokenResolver: ((value: TokenResult) => void) | null = null;
	let tokenRejecter: ((reason: string) => void) | null = null;

	const config: GatewayConfiguration = {
		name: Feature.metadata<string>(FeatureFlag.SQUARE_CASHAPP, 'title') ?? getLocaleText('Cash App'),
		gatewayId: 'peachpay_square_cashapp',
		description: getDescription(),
		assets: {
			title: {src: cashappImageURL},
			badge: {src: cashappImageLogoOnlyURL},
		},
		hasCustomButton: true,
	};

	const debouncedActivate = debounce((async (payments: Payments, paymentRequest: PaymentRequest) => {
		if (cashapp) {
			return;
		}

		if (PaymentConfiguration.eligibleGateway(config.gatewayId) !== GatewayEligibility.Eligible) {
			config.initialized = false;
			store.dispatch(render());
			return;
		}

		try {
			cashapp = await payments.cashAppPay(paymentRequest, {
				redirectURL: window.top?.location.href ?? '',
			});

			cashapp.addEventListener('ontokenization', e => {
				const detail = e.detail as TokenizationEvent;

				if (detail.tokenResult && tokenResolver) {
					tokenResolver(detail.tokenResult);
				} else if (tokenRejecter) {
					tokenRejecter('An unknown tokenization error occured');
				}
			});

			cashapp.addEventListener('customerDismissed', _e => {
				if (tokenRejecter) {
					tokenRejecter('Customer closed cashapp popup');
				}
			});

			config.initialized = true;
			store.dispatch(render());
		} catch (error: unknown) {
			config.initialized = false;
			store.dispatch(render());

			console.error(error);
			captureSentryException(error as Error);
		}
	}) satisfies SquareGateway['activate']);

	return {
		config,
		activate: debouncedActivate,
		reset() {
			clearInterval(buttonInstanceWaitingInterval);

			if (!cashapp) {
				return;
			}

			const oldInstance = cashapp;
			cashapp = null;
			tokenResolver = null;
			tokenRejecter = null;

			oldInstance?.destroy()?.catch(error => {
				if (error instanceof PaymentMethodAlreadyDestroyedError) {
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		attach(selector: string) {
			clearInterval(buttonInstanceWaitingInterval);
			buttonInstanceWaitingInterval = setInterval(() => {
				if (cashapp) {
					clearInterval(buttonInstanceWaitingInterval);

					cashapp?.attach(selector, {
						shape: 'semiround',
						size: 'medium',
					})?.catch(error => {
						config.initialized = false;
						store.dispatch(render());

						if (error instanceof PaymentMethodUnsupportedError) {
							console.log(error);
							return;
						}

						console.error(error);
						captureSentryException(error as Error, {selector});
					});
				}
			}, 200);
		},
		detach() {
			clearInterval(buttonInstanceWaitingInterval);
			cashapp?.detach()?.catch(error => {
				if (error instanceof PaymentMethodNotAttachedError) {
					return;
				}

				config.initialized = false;
				store.dispatch(render());

				if (error instanceof PaymentMethodAlreadyDestroyedError) {
					cashapp = null;
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		async tokenize(): Promise<ProcessedTokenResult> {
			try {
				if (!cashapp) {
					return [null, null, {
						paymentStatus: 'creationfailed',
						note: 'Failed to find Cash App instance while attempting to tokenize the payment.',
					}];
				}

				const tokenResult = new Promise<TokenResult>((resolve, reject) => {
					tokenResolver = resolve;
					tokenRejecter = reject;
				});

				return handleTokenResult(await tokenResult);
			} catch (error: unknown) {
				const update: ITransactionUpdate = {
					paymentStatus: 'creationfailed',
					note: 'An unknown exception occured while tokenizing the payment.',
				};

				if (error === 'Customer closed cashapp popup') {
					update.note = error;
				}

				if (error instanceof Error) {
					update.note = error.message;
				}

				return [null, null, update];
			}
		},
	};
}

function getDescription(): string {
	return /* html */ `
<img style="display: block; text-align: left; height: 1.5rem; " src=${cashappImageURL}>
<p style="text-align: left; margin: 0.5rem 0;">${getLocaleText('Cash App selected for checkout.')}</p>
<hr/>
<p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${
	getLocaleText('After pressing the Cash App button you\'ll be prompted to scan a QR code or approve the payment through the Cash App mobile app.')
}<p>`;
}

export {initSquareCashAppMethod};
